import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import type { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXL } from '@sorare/core/src/atoms/typography';
import { EssenceBalance } from '@sorare/core/src/components/balance/EssenceBalance';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { getRarityColor } from '@sorare/core/src/lib/colors';
import { isType } from '@sorare/core/src/lib/gql';

import { RankingInfo } from '#src/components/so5/LastFixtureStory/CardRewardsReveal/RankingInfo/index.tsx';
import { RewardBaseAnimation } from '#src/components/so5/LastFixtureStory/CardRewardsReveal/RewardBase/RewardBaseAnimation/index.tsx';
import { RewardBaseIcon } from '#src/components/so5/LastFixtureStory/CardRewardsReveal/RewardBase/RewardBaseIcon/index.tsx';
import { RewardBase } from '#src/components/so5/LastFixtureStory/CardRewardsReveal/RewardBase/index.tsx';

import type {
  CardShardsReward_anyRewardConfigInterface,
  CardShardsReward_so5Reward,
} from './__generated__/index.graphql.ts';

const Root = styled.div`
  isolation: isolate;
  position: relative;
  transform-style: preserve-3d;
  aspect-ratio: 210 / 340;
`;
const Foreground = styled.div`
  width: 100%;
  height: 100%;

  transform: translateZ(-1px);

  .revealed & {
    transform: rotateY(180deg) translateZ(0px);
    opacity: 0;
  }
  transition:
    0.4s transform ease-in-out,
    0.4s opacity ease-in-out;
`;
const Background = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  transform: rotateY(180deg) translateZ(1px);
  opacity: 0;

  .revealed & {
    transform: rotateY(360deg) translateZ(1px);
    opacity: 1;
  }
  transition:
    0.4s transform ease-in-out,
    0.4s opacity ease-in-out;
`;
const IconWrapper = styled(Vertical).attrs({ center: true })`
  & > :first-child {
    z-index: -1;
    margin-bottom: calc(var(--double-unit) * -1);
  }
`;

type Props = {
  so5Reward: CardShardsReward_so5Reward;
  cardShardsConfig: CardShardsReward_anyRewardConfigInterface;
  leaguePictureUrl: string;
  onReveal?: () => void;
  revealed?: boolean;
};
export const CardShardsReward = ({
  so5Reward,
  cardShardsConfig,
  leaguePictureUrl,
  onReveal,
  revealed,
}: Props) => {
  const { currentUser } = useCurrentUserContext();

  if (!isType(cardShardsConfig, 'CardShardRewardConfig')) {
    return null;
  }

  const { so5Leaderboard, so5Ranking } = so5Reward;

  const rarityColor = revealed
    ? getRarityColor(cardShardsConfig.rarity)
    : getRarityColor(so5Leaderboard.rarityType as Rarity);
  const props = {
    color: rarityColor,
    borderColor: `linear-gradient(to bottom, ${rarityColor}, var(--c-black) 8%, var(--c-black) 92%, ${rarityColor})`,
  };

  const shardCount = currentUser?.cardShardsChests?.find(
    ({ rarity }: { rarity: Rarity }) => rarity === cardShardsConfig.rarity
  )?.cardShardsCount;

  return (
    <Root className={classNames({ revealed })}>
      <Background>
        <RewardBase
          {...props}
          background="linear-gradient(to right, var(--c-nl-500), var(--c-nl-500)), conic-gradient(from 90deg at 3px 3px, var(--c-nd-100) 25%, var(--c-black) 0) 0 0/5px 5px, var(--c-black)"
          topRightIcon={
            !!so5Leaderboard.iconUrl && (
              <img height={40} src={so5Leaderboard.iconUrl} alt="" />
            )
          }
          topLeftIcon={
            !!shardCount && (
              <EssenceBalance
                rarity={cardShardsConfig.rarity}
                value={shardCount}
              />
            )
          }
          icon={
            <RewardBaseAnimation revealed={revealed}>
              <IconWrapper>
                <RewardBaseIcon>
                  <RewardConfigImage
                    size={10}
                    anyRewardConfig={cardShardsConfig}
                  />
                </RewardBaseIcon>
                <BrandXL>
                  <RewardConfigText anyRewardConfig={cardShardsConfig} />
                </BrandXL>
              </IconWrapper>
            </RewardBaseAnimation>
          }
          body={
            <RewardBaseAnimation revealed={revealed}>
              <RankingInfo so5Ranking={so5Ranking} />
            </RewardBaseAnimation>
          }
          leaguePictureUrl={leaguePictureUrl}
          revealed
        />
      </Background>
      <Foreground>
        <RewardBase
          {...props}
          background="linear-gradient(to right, var(--c-nl-500), var(--c-nl-500)), conic-gradient(from 90deg at 3px 3px, var(--c-nd-100) 25%, var(--c-black) 0) 0 0/5px 5px, var(--c-black)"
          icon={
            <Vertical>
              {!!cardShardsConfig && (
                <RewardBaseIcon>
                  <RewardConfigImage
                    size={10}
                    anyRewardConfig={cardShardsConfig}
                  />
                </RewardBaseIcon>
              )}
            </Vertical>
          }
          leaguePictureUrl={leaguePictureUrl}
          onReveal={onReveal}
        />
      </Foreground>
    </Root>
  );
};

CardShardsReward.fragments = {
  anyRewardConfigInterface: gql`
    fragment CardShardsReward_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ... on CardShardRewardConfig {
        id
        rarity
      }
      ...RewardConfigImage_anyRewardConfigInterface
      ...RewardConfigText_anyRewardConfigInterface
    }
    ${RewardConfigImage.fragments.anyRewardConfigInterface}
    ${RewardConfigText.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<CardShardsReward_anyRewardConfigInterface>,
  so5Reward: gql`
    fragment CardShardsReward_so5Reward on So5Reward {
      slug
      id
      so5Leaderboard {
        slug
        rarityType
        iconUrl
      }
      so5Ranking {
        id
        ...RankingInfo_so5Ranking
      }
    }
    ${RankingInfo.fragments.so5Ranking}
  ` as TypedDocumentNode<CardShardsReward_so5Reward>,
};
